import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, input, output, inject } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

// PrimeNG imports
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TagModule } from 'primeng/tag';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { TooltipModule } from 'primeng/tooltip';

// Models and services
import { ToastMessageService } from '@fitness-force/api';
import { ConfirmationService } from 'primeng/api';

// Import sub-components
import { BrandFormComponent } from '../brand-form/brand-form.component';
import { BrandResponse, ListBrandsResponse } from '../../models';
import { BrandService } from '../../services';
import { Store } from '@ngrx/store';
import { TENANTS_INFO } from '@fitness-force/state';
import type { TENANTSINTERFACE } from '@fitness-force/models';
@Component({
  selector: 'lib-brand-list',
  standalone: true,
  imports: [
    FormsModule,
    TranslateModule,
    DialogModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    ToastModule,
    ConfirmDialogModule,
    TagModule,
    IconFieldModule,
    InputIconModule,
    TooltipModule,
    BrandFormComponent
],
  providers: [ConfirmationService],
  template: `
    <p-dialog
      [visible]="visible()"
      [modal]="true"
      [closable]="true"
      [draggable]="false"
      [resizable]="false"
      [style]="{ width: '800px', height: '80vh' }"
      (onHide)="close()">
    
      <ng-template pTemplate="header">
        <h3>{{ 'RETAIL_BILLING.BRAND.MANAGE' | translate }}</h3>
      </ng-template>
    
      <div class="card">
        <div class="flex justify-content-between align-items-center mb-4">
          <p-iconField iconPosition="left">
            <p-inputIcon styleClass="pi pi-search" />
            <input
              pInputText
              type="text"
              [placeholder]="'common.PLACEHOLDER.SEARCH' | translate"
              [(ngModel)]="searchTerm"
              (ngModelChange)="onSearch()"
              aria-label="search-brands-input" />
            </p-iconField>
    
            <p-button [label]="'RETAIL_BILLING.BRAND.CREATE' | translate"
              icon="pi pi-plus"
              (onClick)="showCreateDialog()"
              aria-label="create-brand-button" />
          </div>
    
          <p-table
            [value]="brands"
            [loading]="loading"
            [paginator]="true"
            [lazy]="true"
            [rows]="pageSize"
            [(first)]="first"
            [totalRecords]="totalRecords"
            (onPage)="onPageChange($event)"
            [showCurrentPageReport]="true"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
            [rowsPerPageOptions]="[10, 25, 50]"
            paginatorDropdownAppendTo="body"
            styleClass="p-datatable-gridlines">
    
            <ng-template pTemplate="header">
              <tr>
                <th>{{ 'RETAIL_BILLING.BRAND.NAME' | translate }}</th>
                <th>{{ 'RETAIL_BILLING.PRODUCTS.STATUS' | translate }}</th>
                <th>{{ 'RETAIL_BILLING.COMMON.ACTIONS' | translate }}</th>
              </tr>
            </ng-template>
    
            <ng-template pTemplate="body" let-brand>
              <tr>
                <td>
                  <div>
                    <div class="font-medium">{{ brand.name_primary_language }}</div>
                    <div class="text-sm text-500">{{ brand.name_secondary_language }}</div>
                  </div>
                </td>
                <td>
                  <p-tag [value]="brand.status ? ('RETAIL_BILLING.PRODUCTS.ACTIVE' | translate) : ('RETAIL_BILLING.PRODUCTS.INACTIVE' | translate)"
                    [severity]="brand.status ? 'success' : 'danger'" />
                </td>
                <td>
                  <div class="flex gap-2">
                    <p-button [icon]="brand.status ? 'pi pi-check-circle' : 'pi pi-times-circle'"
                      [pTooltip]="brand.status ? ('RETAIL_BILLING.COMMON.MARK_INACTIVE' | translate) : ('RETAIL_BILLING.COMMON.MARK_ACTIVE' | translate)"
                      tooltipPosition="top"
                      [text]="true"
                      [rounded]="true"
                      [outlined]="true"
                      size="small"
                      [severity]="brand.status ? 'success' : 'danger'"
                      (onClick)="toggleBrandStatus(brand)"
                      [ariaLabel]="'toggle-status-brand-' + brand.id" />
                    <p-button icon="pi pi-pencil"
                      [pTooltip]="'RETAIL_BILLING.COMMON.EDIT' | translate"
                      tooltipPosition="top"
                      [text]="true"
                      [rounded]="true"
                      [outlined]="true"
                      size="small"
                      (onClick)="editBrand(brand)"
                      [ariaLabel]="'edit-brand-' + brand.id" />
                  </div>
                </td>
              </tr>
            </ng-template>
    
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="3" class="text-center">{{ 'RETAIL_BILLING.BRAND.NO_BRANDS' | translate }}</td>
              </tr>
            </ng-template>
          </p-table>
        </div>
    
        <ng-template pTemplate="footer">
          <div class="flex justify-content-end">
            <p-button [label]="'RETAIL_BILLING.COMMON.CLOSE' | translate"
              icon="pi pi-times"
              [text]="true"
              (onClick)="close()"
              aria-label="close-button" />
          </div>
        </ng-template>
      </p-dialog>
    
      <!-- Create/Edit Brand Dialog -->
      @if (showBrandDialog) {
        <lib-brand-form [visible]="showBrandDialog"
          [brand]="selectedBrand"
          [companyLevel]="companyLevel()"
          [tenantId]="tenantId()"
          [primaryLanguage]="primaryLanguage()"
          [secondaryLanguage]="secondaryLanguage()"
          [isSecondaryLanguage]="isSecondaryLanguage()"
          (onSave)="onBrandSaved($event)"
          (onCancel)="hideBrandDialog()" />
      }
    
      <p-confirmDialog />
      <p-toast />
    `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BrandListComponent implements OnInit, OnDestroy {
  private readonly brandService = inject(BrandService);
  private readonly toastService = inject(ToastMessageService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly translate = inject(TranslateService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly store = inject(Store);

  readonly visible = input(false);
  readonly companyLevel = input<boolean>(true);
  readonly tenantId = input<number>();
  readonly primaryLanguage = input<string>('');
  readonly secondaryLanguage = input<string>('');
  readonly isSecondaryLanguage = input<boolean>(false);
  readonly visibleChange = output<boolean>();
  readonly onClose = output<void>();

  brands: BrandResponse[] = [];
  loading = false;
  totalRecords = 0;
  pageSize = 10;
  pageNumber = 1;
  searchTerm = '';
  first = 0;
  showBrandDialog = false;
  selectedBrand: BrandResponse | null = null;

  private destroy$ = new Subject<void>();
  private searchSubject$ = new Subject<string>();

  ngOnInit() {
    this.searchSubject$
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(() => {
        this.pageNumber = 1;
        this.first = 0;
        this.loadBrands();
      });

    this.store.select(TENANTS_INFO)
      .pipe(takeUntil(this.destroy$))
      .subscribe((tenant: TENANTSINTERFACE) => {
        if (tenant.selected_tenant.id > 0) {
          this.store.dispatch({ type: 'ROUTER_GO' });
          this.loadBrands();
        }
      });
  }

  loadBrands() {
    this.loading = true;
    this.brandService.getCompanyBrands({
      page: this.pageNumber,
      page_size: this.pageSize,
      search_term: this.searchTerm || undefined,
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: ListBrandsResponse) => {
          this.brands = response.items || [];
          this.totalRecords = response.total_items;
          if (response.page_size) {
            this.pageSize = response.page_size;
          }
          if (response.current_page) {
            this.pageNumber = response.current_page;
            this.first = (response.current_page - 1) * this.pageSize;
          }
          this.loading = false;
          this.changeDetectorRef.detectChanges();
        },
        error: (error) => {
          this.toastService.showError(error);
          this.loading = false;
          this.changeDetectorRef.detectChanges();
        }
      });
  }

  onSearch() {
    this.searchSubject$.next(this.searchTerm);
  }

  onPageChange(e: { first: number; rows: number }) {
    this.pageSize = e.rows;
    this.first = e.first;
    this.pageNumber = e.first ? e.first / this.pageSize + 1 : 1;
    this.loadBrands();
  }

  showCreateDialog() {
    this.selectedBrand = null;
    this.showBrandDialog = true;
  }

  editBrand(brand: BrandResponse) {
    this.selectedBrand = brand;
    this.showBrandDialog = true;
  }

  toggleBrandStatus(brand: BrandResponse) {
    const newStatus = !brand.status;
    const confirmMessage = newStatus 
      ? this.translate.instant('RETAIL_BILLING.COMMON.CONFIRM_ACTIVATE')
      : this.translate.instant('RETAIL_BILLING.COMMON.CONFIRM_DEACTIVATE');
    
    this.confirmationService.confirm({
      message: confirmMessage,
      header: this.translate.instant('RETAIL_BILLING.COMMON.CONFIRM'),
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        const updatedBrand = {
          ...brand,
          status: newStatus
        };
        
        this.brandService.updateCompanyBrand(brand.id, updatedBrand)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              const successMessage = newStatus
                ? this.translate.instant('RETAIL_BILLING.BRAND.ACTIVATED_SUCCESS')
                : this.translate.instant('RETAIL_BILLING.BRAND.DEACTIVATED_SUCCESS');
              this.toastService.showSuccess(successMessage);
              this.loadBrands();
            },
            error: (error) => {
              this.toastService.showError(error);
            }
          });
      }
    });
  }

  onBrandSaved(brand: BrandResponse) {
    this.hideBrandDialog();
    this.loadBrands();
    const message = this.selectedBrand 
      ? 'RETAIL_BILLING.BRAND.UPDATE_SUCCESS'
      : 'RETAIL_BILLING.BRAND.CREATE_SUCCESS';
    this.toastService.showSuccess(this.translate.instant(message));
  }

  hideBrandDialog() {
    this.showBrandDialog = false;
    this.selectedBrand = null;
  }

  close(): void {
    this.onClose.emit();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.visibleChange.emit(false);
  }
}